import React from 'react';

import API from '@beardeddevops/react.api';
import Types from 'constants/Types';

export const getAddOnPrice = (price) => {
	const parsedPrice = Number.parseFloat(String(price || '').replace(/[^0-9.]/g, ''));
	return Number.isNaN(parsedPrice) ? 0 : parsedPrice;
};

export const normalizeSignupAddOnSlug = (slug) => {
	const value = String(slug || '').trim().toLowerCase();
	if (/^kt[_-]/.test(value)) {
		return 'kt-' + value.slice(3).replace(/_/g, '-');
	}
	return value;
};

export const isYearlyAddOn = (slug) => /^kt-/.test(normalizeSignupAddOnSlug(slug));

const panelStyles = {
	panel: {
		background: '#f8fafc',
		border: '1px solid #e2e8f0',
		borderRadius: '16px',
		boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)',
		overflow: 'hidden'
	},
	header: {
		background: '#fff',
		borderBottom: '1px solid #e2e8f0',
		padding: '18px 20px 16px'
	},
	title: {
		color: '#172033',
		fontSize: '20px',
		fontWeight: 700,
		margin: 0
	},
	intro: {
		color: '#64748b',
		fontSize: '12px',
		lineHeight: 1.5,
		margin: '5px 0 0'
	},
	body: {
		padding: '14px'
	},
	list: {
		maxHeight: '430px',
		overflowY: 'auto',
		paddingRight: '4px'
	},
	card: {
		alignItems: 'center',
		background: '#fff',
		border: '1px solid #e2e8f0',
		borderRadius: '12px',
		cursor: 'pointer',
		display: 'flex',
		gap: '11px',
		margin: '0 0 10px',
		minHeight: '76px',
		padding: '12px',
		position: 'relative',
		transition: 'all 160ms ease'
	},
	cardSelected: {
		background: 'linear-gradient(135deg, #eff6ff 0%, #f8fbff 100%)',
		border: '1px solid #3b82f6',
		boxShadow: '0 5px 16px rgba(59, 130, 246, 0.14)'
	},
	cardDisabled: {
		cursor: 'not-allowed',
		opacity: 0.55
	},
	checkbox: {
		accentColor: '#2563eb',
		cursor: 'pointer',
		flex: '0 0 22px',
		height: '22px',
		margin: 0,
		width: '22px'
	},
	icon: {
		alignItems: 'center',
		background: '#eaf2ff',
		borderRadius: '10px',
		color: '#2563eb',
		display: 'flex',
		flex: '0 0 38px',
		fontSize: '17px',
		height: '38px',
		justifyContent: 'center',
		width: '38px'
	},
	details: {
		display: 'flex',
		flex: '1 1 auto',
		flexDirection: 'column',
		minWidth: 0
	},
	name: {
		color: '#172033',
		fontSize: '14px',
		fontWeight: 700,
		lineHeight: 1.25
	},
	description: {
		color: '#64748b',
		fontSize: '11px',
		lineHeight: 1.35,
		marginTop: '3px'
	},
	price: {
		color: '#64748b',
		flex: '0 0 auto',
		fontSize: '10px',
		lineHeight: 1.15,
		textAlign: 'right'
	},
	priceAmount: {
		color: '#0f172a',
		fontSize: '18px',
		fontWeight: 800,
		letterSpacing: '-0.4px'
	},
	regularPrice: {
		color: '#94a3b8',
		fontSize: '10px',
		marginRight: '4px',
		textDecoration: 'line-through'
	},
	savings: {
		background: '#dcfce7',
		borderRadius: '999px',
		color: '#15803d',
		display: 'inline-block',
		fontSize: '9px',
		fontWeight: 700,
		marginTop: '4px',
		padding: '3px 6px',
		textTransform: 'uppercase'
	},
	total: {
		alignItems: 'center',
		background: '#fff',
		border: '1px solid #dbe4ee',
		borderRadius: '12px',
		display: 'flex',
		justifyContent: 'space-between',
		marginTop: '14px',
		padding: '14px 16px'
	},
	totalLabel: {
		color: '#64748b',
		fontSize: '11px',
		fontWeight: 600,
		textTransform: 'uppercase'
	},
	totalPrice: {
		color: '#152b4f',
		fontSize: '21px',
		fontWeight: 800,
		letterSpacing: '-0.5px'
	},
	billingNote: {
		alignItems: 'flex-start',
		color: '#64748b',
		display: 'flex',
		fontSize: '11px',
		gap: '7px',
		lineHeight: 1.4,
		margin: '11px 3px 1px'
	},
	placeholderLine: {
		background: '#edf1f5',
		borderRadius: '3px',
		height: '13px',
		margin: '12px 0'
	},
	addOnsMessage: {
		color: '#64748b',
		fontSize: '12px',
		padding: '28px 12px',
		textAlign: 'center'
	}
};

export default class SignupAddOns extends React.Component {
	static defaultProps = {
		autoLoad: true,
		showTotals: true,
		billingNote: 'Secure billing is completed through Stripe after account creation.',
		intro: 'Bundle add-ons with your plan today to receive signup pricing. Add-ons purchased after signup will be billed at the standard rate.',
		title: 'Add-ons',
		selectedAddOns: []
	};

	static preloadPromise = null;
	static preloadedAddOns = null;
	static preloadError = false;
	static preloadedPricing = {
		plans: {},
		bundle_discounting: true,
		bundle_discount: null
	};

	static applyPricingFromResponse = (response) => {
		SignupAddOns.preloadedPricing = {
			plans: (response && response.plans) || {},
			bundle_discounting: response ? response.bundle_discounting !== false : true,
			bundle_discount: (response && response.bundle_discount) || null
		};
	};

	static mapAddOnItems = (items) => (
		Array.isArray(items)
			? items.map((addOn) => ({
				value: normalizeSignupAddOnSlug(addOn.slug),
				label: addOn.name,
				description: addOn.description,
				monthlyPrice: getAddOnPrice(addOn.signup_price),
				regularPrice: getAddOnPrice(addOn.standard_price),
				icon: addOn.icon || 'fa-puzzle-piece',
				requiresSlug: addOn.requires_slug || null,
				stripePlanId: addOn.stripe_plan_id || null,
				modulePkey: addOn.module_pkey || null
			}))
			: []
	);

	static preload = async () => {
		if (SignupAddOns.preloadPromise) {
			return SignupAddOns.preloadPromise;
		}

		const signupAddOnsModel = new API.BuildClass(Types.Signup_Addons._name, API.RequestType.READ_ALL);
		const query = {
			signup_url: typeof window !== 'undefined' ? window.location.href : '',
			is_tactical: typeof window !== 'undefined' && window.isTacticalPay ? 1 : 0
		};
		SignupAddOns.preloadPromise = signupAddOnsModel.submit(query, API.RequestType.READ_ALL)
			.then((response) => {
				SignupAddOns.preloadedAddOns = SignupAddOns.mapAddOnItems(response && response.items);
				SignupAddOns.applyPricingFromResponse(response);
				SignupAddOns.preloadError = false;
				return SignupAddOns.preloadedAddOns;
			})
			.catch(() => {
				SignupAddOns.preloadedAddOns = [];
				SignupAddOns.preloadError = true;
				return SignupAddOns.preloadedAddOns;
			});

		return SignupAddOns.preloadPromise;
	};

	constructor(props) {
		super(props);
		this.state = {
			signupAddOns: [],
			signupAddOnsLoading: true,
			signupAddOnsError: false
		};
	}

	componentDidMount() {
		if (SignupAddOns.preloadPromise) {
			SignupAddOns.preloadPromise.then(() => {
				this.setState({
					signupAddOns: SignupAddOns.preloadedAddOns || [],
					signupAddOnsLoading: false,
					signupAddOnsError: SignupAddOns.preloadError
				}, this.notifyAvailability);
			});
			return;
		}

		if (this.props.autoLoad) {
			this.loadSignupAddOns();
		}
		this.notifyAvailability();
	}

	componentDidUpdate(prevProps, prevState) {
		if (
			prevState.signupAddOnsLoading !== this.state.signupAddOnsLoading
			|| prevState.signupAddOnsError !== this.state.signupAddOnsError
			|| prevState.signupAddOns.length !== this.state.signupAddOns.length
		) {
			this.notifyAvailability();
		}
	}

	isPanelVisible = () => {
		const {signupAddOns, signupAddOnsLoading, signupAddOnsError} = this.state;
		return signupAddOnsLoading || signupAddOnsError || signupAddOns.length > 0;
	};

	notifyAvailability = () => {
		if (typeof this.props.onAvailabilityChange === 'function') {
			this.props.onAvailabilityChange(this.isPanelVisible());
		}
		if (typeof this.props.onPricingChange === 'function') {
			this.props.onPricingChange(SignupAddOns.preloadedPricing);
		}
	};

	loadSignupAddOns = async () => {
		const signupAddOnsModel = new API.BuildClass(Types.Signup_Addons._name, API.RequestType.READ_ALL);

		try {
			const response = await signupAddOnsModel.submit({
				signup_url: typeof window !== 'undefined' ? window.location.href : '',
				is_tactical: typeof window !== 'undefined' && window.isTacticalPay ? 1 : 0
			}, API.RequestType.READ_ALL);
			const signupAddOns = SignupAddOns.mapAddOnItems(response && response.items);
			SignupAddOns.preloadedAddOns = signupAddOns;
			SignupAddOns.applyPricingFromResponse(response);
			SignupAddOns.preloadError = false;
			this.setState({signupAddOns, signupAddOnsLoading: false, signupAddOnsError: false}, this.notifyAvailability);
		} catch (error) {
			SignupAddOns.preloadedAddOns = [];
			SignupAddOns.preloadError = true;
			this.setState({signupAddOns: [], signupAddOnsLoading: false, signupAddOnsError: true}, this.notifyAvailability);
		}
	};

	handleAddOnChange = (addOn) => {
		const {onSelectedAddOnsChange} = this.props;
		const selectedAddOns = Array.isArray(this.props.selectedAddOns) ? this.props.selectedAddOns : [];
		const signupAddOns = Array.isArray(this.state.signupAddOns) ? this.state.signupAddOns : [];
		const isSelected = selectedAddOns.some((selectedAddOn) => selectedAddOn && selectedAddOn.value === addOn.value);
		const parentSelected = !addOn.requiresSlug || selectedAddOns.some((selectedAddOn) => selectedAddOn && selectedAddOn.value === addOn.requiresSlug);

		if (!isSelected && addOn.requiresSlug && !parentSelected) {
			return;
		}

		if (isSelected) {
			if (typeof onSelectedAddOnsChange === 'function') {
				onSelectedAddOnsChange(selectedAddOns.filter((selectedAddOn) => (
					selectedAddOn
					&& selectedAddOn.value !== addOn.value
					&& selectedAddOn.requiresSlug !== addOn.value
				)));
			}
			return;
		}

		let nextSelectedAddOns = [...selectedAddOns, addOn];
		if (addOn.requiresSlug) {
			const parentAddOn = signupAddOns.find((item) => item.value === addOn.requiresSlug);
			const hasParent = nextSelectedAddOns.some((selectedAddOn) => selectedAddOn && selectedAddOn.value === addOn.requiresSlug);
			if (parentAddOn && !hasParent) {
				nextSelectedAddOns = [...nextSelectedAddOns, parentAddOn];
			}
		}

		if (typeof onSelectedAddOnsChange === 'function') {
			onSelectedAddOnsChange(nextSelectedAddOns);
		}
	};

	render() {
		if (!this.isPanelVisible()) {
			return null;
		}

		const {
			selectedAddOns,
			basePlanPrice,
			hasBasePlan,
			showTotals,
			billingNote,
			intro,
			title
		} = this.props;
		const {signupAddOns, signupAddOnsLoading, signupAddOnsError} = this.state;
		const safeSelectedAddOns = Array.isArray(selectedAddOns) ? selectedAddOns : [];
		const selectedMonthlyAddOnTotal = safeSelectedAddOns.reduce((total, addOn) => (
			isYearlyAddOn(addOn.value) ? total : total + getAddOnPrice(addOn.monthlyPrice)
		), 0);
		const selectedYearlyAddOnTotal = safeSelectedAddOns.reduce((total, addOn) => (
			isYearlyAddOn(addOn.value) ? total + getAddOnPrice(addOn.monthlyPrice) : total
		), 0);
		const selectedMonthlySavings = safeSelectedAddOns.reduce((total, addOn) => (
			isYearlyAddOn(addOn.value) ? total : total + Math.max(0, getAddOnPrice(addOn.regularPrice) - getAddOnPrice(addOn.monthlyPrice))
		), 0);
		const selectedYearlySavings = safeSelectedAddOns.reduce((total, addOn) => (
			isYearlyAddOn(addOn.value) ? total + Math.max(0, getAddOnPrice(addOn.regularPrice) - getAddOnPrice(addOn.monthlyPrice)) : total
		), 0);
		const monthlyBasePlanPrice = getAddOnPrice(basePlanPrice);

		return (
			<section aria-labelledby="signup-add-ons-heading" style={panelStyles.panel}>
				<header style={panelStyles.header}>
					<h4 id="signup-add-ons-heading" style={panelStyles.title}>{title}</h4>
					{intro && <p style={panelStyles.intro}>{intro}</p>}
				</header>
				<div style={panelStyles.body}>
					<div style={panelStyles.list}>
						{signupAddOnsLoading && [1, 2, 3].map((placeholder) => (
							<div key={placeholder} style={{...panelStyles.card, cursor: 'default'}}>
								<span style={{...panelStyles.checkbox, background: '#f1f5f9', borderRadius: '4px'}}/>
								<span style={{...panelStyles.icon, background: '#f1f5f9', color: '#f1f5f9'}}/>
								<span style={{...panelStyles.placeholderLine, flex: 1, margin: 0}}/>
							</div>
						))}
						{signupAddOnsError && <div style={panelStyles.addOnsMessage}>Add-ons are temporarily unavailable. Please try again.</div>}
						{!signupAddOnsLoading && !signupAddOnsError && signupAddOns.map((addOn) => {
							const isSelected = safeSelectedAddOns.some((selectedAddOn) => selectedAddOn && selectedAddOn.value === addOn.value);
							const parentSelected = !addOn.requiresSlug || safeSelectedAddOns.some((selectedAddOn) => selectedAddOn && selectedAddOn.value === addOn.requiresSlug);
							const isDisabled = !!addOn.requiresSlug && !parentSelected;
							const isYearly = isYearlyAddOn(addOn.value);
							const pricePeriod = isYearly ? 'yr/license' : 'mo';
							const savings = addOn.regularPrice - addOn.monthlyPrice;
							return (
								<label key={addOn.value} style={{
									...panelStyles.card,
									...(isSelected ? panelStyles.cardSelected : {}),
									...(isDisabled ? panelStyles.cardDisabled : {})
								}}>
									<input
										type="checkbox"
										checked={isSelected}
										disabled={isDisabled}
										onChange={() => this.handleAddOnChange(addOn)}
										style={panelStyles.checkbox}
									/>
									<span style={panelStyles.icon}><i className={`fa ${addOn.icon}`}/></span>
									<span style={panelStyles.details}>
										<strong style={panelStyles.name}>{addOn.label}</strong>
										<small style={panelStyles.description}>{addOn.description}</small>
									</span>
									<span style={panelStyles.price}>
										<div>{savings > 0 && <span style={panelStyles.regularPrice}>${addOn.regularPrice}</span>}<span style={panelStyles.priceAmount}>${addOn.monthlyPrice}</span>/{pricePeriod}</div>
										{savings > 0 && <span style={panelStyles.savings}>Save ${savings}</span>}
									</span>
								</label>
							);
						})}
					</div>
					{showTotals && (
						<>
							<div style={panelStyles.total}>
								<div>
									<div style={panelStyles.totalLabel}>Monthly total</div>
									{selectedMonthlySavings > 0 && <small style={{color: '#15803d'}}>You save ${selectedMonthlySavings}/mo</small>}
								</div>
								<strong style={panelStyles.totalPrice}>{hasBasePlan ? `$${(monthlyBasePlanPrice + selectedMonthlyAddOnTotal).toFixed(2)}` : 'Select plan'}<small style={{fontSize: '11px', fontWeight: 500}}>/mo</small></strong>
							</div>
							{selectedYearlyAddOnTotal > 0 && <div style={{...panelStyles.total, marginTop: '8px', flexDirection: 'column', alignItems: 'stretch'}}>
								<div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}>
									<div>
										<div style={panelStyles.totalLabel}>Yearly total*</div>
										{selectedYearlySavings > 0 && <small style={{color: '#15803d'}}>You save ${selectedYearlySavings}/yr</small>}
									</div>
									<strong style={panelStyles.totalPrice}>${selectedYearlyAddOnTotal.toFixed(2)}<small style={{fontSize: '11px', fontWeight: 500}}>/yr</small></strong>
								</div>
								<small style={{color: '#64748b', display: 'block', marginTop: '8px', fontWeight: 400, textTransform: 'none', lineHeight: 1.4}}>
									You can adjust AV license quantities before purchasing after signup.
								</small>
								<small style={{color: '#64748b', display: 'block', marginTop: '4px', fontWeight: 400, textTransform: 'none', lineHeight: 1.4}}>
									*Yearly total assumes 1 license of each selected AV product.
								</small>
							</div>}
						</>
					)}
					{billingNote && <p style={panelStyles.billingNote}><i className="fa fa-lock" style={{color: '#2563eb', marginTop: '2px'}}/> {billingNote}</p>}
				</div>
			</section>
		);
	}
}
